import {
  Box,
  Button,
  Card,
  CardBody,
  CardProps,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerOverlay,
  Flex,
  IconButton,
} from '@chakra-ui/react'
import { ListContentNode } from '@helo/cms'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import SimpleBar from 'simplebar-react'

import { CardMessage } from '../../components/CardMessage/CardMessage'
import { ExpandIcon } from '../../components/Icons/ExpandIcon'
import { Tooltip } from '../../components/Tooltip/Tooltip'
import { TypeRenderer } from '../../datatypes/TypeRenderer'
import { ListRowSkeleton } from '../../datatypes/list/ListRowSkeleton'
import { type ListLoadingPreset } from '../../datatypes/list/listLayout'
import { useAppStore } from '../../store/appStore/appStore'
import { type BrowserTabTitleDrawerSuffix } from '../../store/appStore/browserDocumentTitle/browserTabTitleSpec'
import { useViewWidgetState } from '../../store/appStore/view/useViewWidgetState'
import { createWidget } from '../widget-utils'
import {
  type DrawerCardLayout,
  DrawerCardLayoutUtils,
  type DrawerCardMinHeightSize,
} from './drawerCardLayout'
import { DrawerCardListContent } from './drawerCardListContent'
import { DrawerCardState } from './drawerCardState'

type DrawerCardWidgetProps = CardProps & {
  drawerContent?: ReactNode
  drawerHeaderTitle?: string
  expandButtonLabel?: string
  /**
   * When the drawer opens, inserts this segment after the record type and entity id in
   * the browser tab (e.g. `"Milestones"`, `"Details"`).
   */
  browserTabTitleSuffixWhenExpanded?: BrowserTabTitleDrawerSuffix
  fadeOverflow?: boolean
  layout?: DrawerCardLayout
  minHeightSize?: DrawerCardMinHeightSize
  /** When true, shows a fade on the right edge of the card body to hint at more content when scrolling horizontally */
  fadeOverflowRight?: boolean
  noDataText?: string
  itemList?: ListContentNode | unknown[] | null
  isLoading?: boolean
  listLoadingPreset?: ListLoadingPreset
}

export const DrawerCardWidget = ({
  children,
  drawerContent,
  drawerHeaderTitle = '',
  expandButtonLabel = 'See all',
  browserTabTitleSuffixWhenExpanded,
  fadeOverflow = false,
  layout,
  minHeightSize,
  fadeOverflowRight = false,
  noDataText,
  itemList,
  isLoading,
  listLoadingPreset,
  ...props
}: DrawerCardWidgetProps) => {
  const [{ isOpen }, setWidgetState] = useViewWidgetState({
    persist: true,
    state: {
      isOpen: false,
    },
  })

  const btnRef = useRef<HTMLButtonElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const [showRightFade, setShowRightFade] = useState(true)

  const state = DrawerCardState.resolveDrawerCardDisplayState({
    isLoading,
    itemList,
  })
  const resolvedListLoadingPreset =
    listLoadingPreset ?? (fadeOverflowRight ? 'supplementalDrawer' : undefined)
  const usesItemListForDisplay = DrawerCardListContent.shouldRenderFromItemList(
    {
      itemList,
      listLoadingPreset: resolvedListLoadingPreset,
    },
  )
  const hasDisplayableItems =
    DrawerCardListContent.hasDisplayableItems(itemList)
  const showListSkeleton = DrawerCardListContent.shouldShowListSkeleton({
    itemList,
    listLoadingPreset: resolvedListLoadingPreset,
    isNoData: state === 'no-data',
  })
  const listSkeletonCount = DrawerCardListContent.getSkeletonCount(itemList, 1)
  const itemListContentNode =
    usesItemListForDisplay &&
    hasDisplayableItems &&
    DrawerCardListContent.isResolvedListContentNode(itemList)
      ? DrawerCardListContent.resolveListContentNode(
          itemList,
          resolvedListLoadingPreset!,
        )
      : null

  const updateRightFade = useCallback(() => {
    const el = scrollContainerRef.current
    if (!el) return
    const hasOverflow = el.scrollWidth > el.clientWidth
    const isScrolledToRight =
      el.scrollLeft + el.clientWidth >= el.scrollWidth - 1
    setShowRightFade(hasOverflow && !isScrolledToRight)
  }, [])

  useEffect(() => {
    if (!browserTabTitleSuffixWhenExpanded) return
    useAppStore.getState().browserDocumentTitle.updateTitle({
      detailDrawerSuffix: isOpen ? browserTabTitleSuffixWhenExpanded : null,
    })
    return () => {
      useAppStore.getState().browserDocumentTitle.updateTitle({
        detailDrawerSuffix: null,
      })
    }
  }, [isOpen, browserTabTitleSuffixWhenExpanded])

  useEffect(() => {
    if (!fadeOverflowRight) return
    const el = scrollContainerRef.current
    if (!el) return
    updateRightFade()
    const ro = new ResizeObserver(updateRightFade)
    ro.observe(el)
    return () => ro.disconnect()
  }, [fadeOverflowRight, updateRightFade, state])

  return (
    <Card
      position="relative"
      variant={state === 'no-data' ? 'disabled' : 'default'}
      {...props}
      {...DrawerCardLayoutUtils.minHeight(minHeightSize)}
      {...DrawerCardLayoutUtils.layout(layout)}
    >
      <Drawer
        size="sm"
        isOpen={isOpen}
        placement="right"
        onClose={() => setWidgetState({ isOpen: false })}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton zIndex="999" color="presets.turquoise.100" />
          <SimpleBar style={{ height: '100%' }}>
            <DrawerBody pl="5" py="4" pr="6">
              {drawerContent}
            </DrawerBody>
          </SimpleBar>
        </DrawerContent>
      </Drawer>
      <CardBody
        height="100%"
        position={fadeOverflowRight ? 'relative' : undefined}
      >
        {/**
         * Supplemental drawers render from itemList so state and content stay in sync.
         * Other drawers keep children responsible for their own loading UI.
         */}
        {state === 'no-data' ? (
          <CardMessage message={noDataText} />
        ) : (
          <>
            <SimpleBar style={{ height: '100%' }}>
              <Box
                ref={fadeOverflowRight ? scrollContainerRef : undefined}
                display="flex"
                flexDirection="column"
                pl="5px"
                pr="5px"
                overflowX={fadeOverflowRight ? 'auto' : undefined}
                onScroll={fadeOverflowRight ? updateRightFade : undefined}
              >
                {showListSkeleton && resolvedListLoadingPreset ? (
                  <ListRowSkeleton
                    preset={resolvedListLoadingPreset}
                    count={listSkeletonCount}
                  />
                ) : itemListContentNode ? (
                  <TypeRenderer
                    contentNode={itemListContentNode}
                    isLoading={false}
                  />
                ) : state === 'loading' && resolvedListLoadingPreset ? (
                  <ListRowSkeleton
                    preset={resolvedListLoadingPreset}
                    count={listSkeletonCount}
                  />
                ) : (
                  children
                )}
              </Box>
            </SimpleBar>
            {fadeOverflowRight &&
              (usesItemListForDisplay
                ? hasDisplayableItems
                : state !== 'loading') &&
              showRightFade && (
                <Box
                  position="absolute"
                  right="0"
                  top="0"
                  bottom="0"
                  width="92px"
                  bgGradient="linear(to-l, white, transparent)"
                  pointerEvents="none"
                  zIndex="1"
                  _dark={{
                    bgGradient:
                      'linear(to-l, rgba(255,255,255,0.02), transparent)',
                  }}
                />
              )}
          </>
        )}
      </CardBody>
      {fadeOverflow && state !== 'loading' && (
        <Flex
          position="absolute"
          bgGradient={state === 'done' ? 'linear(to-t, #fff, #ffffff00)' : ''}
          height="137px"
          width="100%"
          bottom="0"
          textAlign="center"
          alignItems="flex-end"
          justifyContent="center"
          paddingBottom="8px"
          pointerEvents="none"
          zIndex="1"
        >
          {state === 'done' ? (
            <Button
              variant="unstyled"
              pointerEvents="auto"
              bg="presets.neutral.20"
              borderRadius="20px"
              px="4px"
              py="2px"
              fontSize="11px"
              fontWeight="normal"
              onClick={() => setWidgetState({ isOpen: true })}
            >
              {expandButtonLabel}
            </Button>
          ) : null}
        </Flex>
      )}
      {state === 'done' && !isLoading && (
        <Flex position="absolute" top="2px" right="2px">
          <Tooltip label={expandButtonLabel}>
            <IconButton
              aria-label={expandButtonLabel}
              ref={btnRef}
              onClick={() => setWidgetState({ isOpen: true })}
              icon={<ExpandIcon boxSize="16px" />}
              zIndex="2"
            />
          </Tooltip>
        </Flex>
      )}
    </Card>
  )
}

export const DrawerCardWidgetDefinition = createWidget(DrawerCardWidget, {
  type: 'DrawerCard',
  slots: ['drawerContent'],
})
